'use client';

import { type UserState } from '@carvana/core-auth';
import type { FavoriteVehicle } from '@carvana/merch-contracts';
import { DefaultLocation, LocationData } from '@carvana/merch-geolocation';
import {
  defaultLoadableState,
  LoadableState,
  useLoadableState
} from '@carvana/merch-react-hooks';
import deepmerge from 'deepmerge';
import React, {
  createContext,
  type PropsWithChildren,
  useCallback,
  useContext,
  useMemo,
  useState
} from 'react';
import type { DeepPartial } from '~/types';
import EventManager, { useEventTrigger } from '~/utilities/EventManager';

export type UserContextType = {
  favoriteChangeFlag: number;
  favorites: LoadableState<FavoriteVehicle[]>;
  geolocationLogging?: {
    googleUtmLocationData?: LocationData | null;
    merchLocationData?: LocationData | null;
  };
  location: LoadableState<LocationData>;
  logInFlag: number;
  logOutFlag: number;
  triggerEvent: EventManager;
  triggerFetchFavorites: () => void;
  triggerFetchUserProfile: () => void;
  userState: LoadableState<Partial<UserState>>;
  userStateFromServer?: Partial<UserState>;
};

export const defaultUserContext: UserContextType = {
  favoriteChangeFlag: 0,
  favorites: defaultLoadableState<FavoriteVehicle[]>([]),
  location: defaultLoadableState<LocationData>(DefaultLocation),
  geolocationLogging: {},
  logInFlag: 0,
  logOutFlag: 0,
  triggerEvent: new EventManager(false),
  triggerFetchFavorites() {},
  triggerFetchUserProfile() {},
  userState: defaultLoadableState<Partial<UserState>>({}),
  userStateFromServer: undefined
};

const UserContext = createContext<UserContextType>(defaultUserContext);

export type OwnProps = PropsWithChildren<{
  geolocationLogging?: {
    googleUtmLocationData?: LocationData | null;
    merchLocationData?: LocationData | null;
  };
  location: LocationData;
  userState?: Partial<UserState>;
}>;

export function UserContextProvider(props: OwnProps) {
  const favorites = useLoadableState<FavoriteVehicle[]>([]);
  const location = useLoadableState<LocationData>(props.location);
  const userState = useLoadableState<Partial<UserState>>(props.userState ?? {});

  const eventManager = useMemo(() => new EventManager(true), []);
  const [favoriteChangeFlag, setFavoritesTrigger] = useState(
    defaultUserContext.favoriteChangeFlag
  );
  const remoteUserRequestFlag = useEventTrigger(eventManager, 'logIn');
  const remoteLogOutFlag = useEventTrigger(eventManager, 'logOut');
  const [localUserRequestFlag, setLocalUserRequestFlag] = useState(0);

  const triggerFetchUserProfile = useCallback(() => {
    setLocalUserRequestFlag(Date.now());
  }, []);

  const triggerFetchFavorites = useCallback(() => {
    setFavoritesTrigger(Date.now());
  }, []);

  const value = useMemo<UserContextType>(
    () => ({
      favoriteChangeFlag,
      favorites,
      geolocationLogging: props?.geolocationLogging,
      location,
      logInFlag: remoteUserRequestFlag - localUserRequestFlag, // Using subtraction to avoid integer overflow
      logOutFlag: remoteLogOutFlag,
      triggerEvent: eventManager,
      triggerFetchFavorites,
      triggerFetchUserProfile,
      userState,
      userStateFromServer: props.userState
    }),
    [
      eventManager,
      favoriteChangeFlag,
      favorites,
      localUserRequestFlag,
      location,
      props?.geolocationLogging,
      props.userState,
      remoteLogOutFlag,
      remoteUserRequestFlag,
      triggerFetchFavorites,
      triggerFetchUserProfile,
      userState
    ]
  );

  return (
    <UserContext.Provider value={value}>{props.children}</UserContext.Provider>
  );
}

export const generateUserContext = (
  customState: DeepPartial<UserContextType>
): UserContextType =>
  deepmerge<UserContextType, DeepPartial<UserContextType>>(
    defaultUserContext,
    customState
  );

export function MockedUserContextProvider({
  children,
  mockValue
}: React.PropsWithChildren<{ mockValue: DeepPartial<UserContextType> }>) {
  return (
    <UserContext.Provider value={generateUserContext(mockValue)}>
      {children}
    </UserContext.Provider>
  );
}

export function useFavorites(): FavoriteVehicle[] {
  return useContext(UserContext).favorites.data;
}

export function useFavoritesIds(): number[] {
  const favorites = useFavorites();

  return useMemo(
    () => favorites.map(favorite => favorite.vehicleId),
    [favorites]
  );
}

export function useIsLoggedIn(): boolean {
  return Boolean(useContext(UserContext).userState.data?.isLoggedIn);
}

export function useGetUser(): Partial<UserState['user']> {
  return useContext(UserContext).userState.data.user ?? {};
}

export function useUserEmail(): string {
  return useContext(UserContext).userState.data.user?.email ?? '';
}

export function useUserId(): string {
  return useContext(UserContext).userState.data.user?.user_id ?? '';
}

export function useUserLocation(): LocationData {
  const context = useContext(UserContext);

  return context.location.data ?? DefaultLocation;
}

export function useUserIsImpersonating(): boolean {
  return (
    useContext(UserContext).userState.data?.impersonation?.is_impersonating ??
    false
  );
}

UserContextProvider.displayName = 'UserContextProvider';

export default UserContext;
